import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { TOPIC_PASS, type TopicScore } from './topicBreakdown';

interface WeakTopicsProps {
  /** Already the weak ones: see `weakTopics`. */
  topics: TopicScore[];
  className?: string;
}

/** The topics of one result that came out under the pass mark, weakest first, with how many were right. */
export function WeakTopics({ topics, className }: WeakTopicsProps) {
  const { t } = useTranslation();
  if (topics.length === 0) return null;

  return (
    <section aria-labelledby="weak-topics-title" className={cn('rounded-lg border border-border bg-card p-5 md:p-6', className)}>
      <h2 id="weak-topics-title" className="font-display text-lg font-bold tracking-[-0.02em] text-foreground">
        {t('result.weak_topics')}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">{t('result.weak_topics_hint', { pass: TOPIC_PASS })}</p>
      <ul className="mt-3">
        {topics.map((topic) => (
          <li key={topic.id} className="border-t border-border py-3 first:border-t-0">
            <div className="flex items-start justify-between gap-3">
              <span className="min-w-0 text-sm font-medium text-foreground [overflow-wrap:anywhere]">{topic.title}</span>
              <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                {topic.correct}/{topic.total} · {topic.percent}%
              </span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-muted" role="img" aria-label={`${topic.title}: ${topic.percent}%`}>
              <div className="h-full rounded-full bg-error-600" style={{ width: `${topic.percent}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default WeakTopics;
